import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import RequestPanel from '../components/RequestPanel';
import Overview from '../components/Overview';
import ElementHighlighter from '../components/AnimatedCursor';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';

// Типы для коллекций и запросов
export type RequestItem = { method: string; path: string; desc?: string; body?: string; recordId?: string };
export type Collection = { name: string; items: RequestItem[] };

export default function ApiRequestPage() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [newCollectionCount, setNewCollectionCount] = useState(1);
  const [openTabs, setOpenTabs] = useState<Array<{ id: string; collectionId: number; requestId: number }>>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [showDemo, setShowDemo] = useState(false);
  const [currentDemoStep, setCurrentDemoStep] = useState<string>('');
  const [, setIsLoadingApiKey] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { logout, isAuthenticated, setApiKey, isLoading: isAuthLoading } = useAuth();
  const settingsMenuRef = useRef<HTMLDivElement>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialLoadRef = useRef(true);
  const hasLoadedRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastSavedCollectionsRef = useRef<string>('');



  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDemoStart = () => {
    setShowDemo(true);
  };

  const handleDemoComplete = () => {
    setShowDemo(false);
    // Очищаем демо-данные
    setCollections([]);
    setOpenTabs([]);
    setActiveTabId(null);
    setNewCollectionCount(1);
  };

  // Демо только после логина: незалогиненных всегда редиректим на login
  useEffect(() => {
    if (isAuthLoading) return;

    if (!isAuthenticated) {
      const isDemoMode = searchParams.get('demo') === 'true';
      navigate('/login', {
        state: { returnTo: isDemoMode ? '/api-request?demo=true' : '/api-request' },
      });
      return;
    }

    const isDemoMode = searchParams.get('demo') === 'true';
    if (isDemoMode) {
      setShowDemo(true);
    }
  }, [isAuthenticated, isAuthLoading, searchParams, navigate]);

  // Закрытие меню настроек при клике вне
  useEffect(() => {
    if (!showSettingsMenu) return;
    function handleClick(e: MouseEvent) {
      if (
        settingsMenuRef.current &&
        !settingsMenuRef.current.contains(e.target as Node)
      ) {
        setShowSettingsMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSettingsMenu]);

  // Функция сохранения collections на сервер
  const saveCollections = async (collectionsToSave: Collection[], signal?: AbortSignal) => {
    if (!isAuthenticated) return;
    
    const token = localStorage.getItem('access_token');
    if (!token) return;

    try {
      await fetch(`${API_URL}/saved-collections`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ collections: collectionsToSave }),
        signal,
      });
    } catch (error) {
      // Игнорируем ошибки отмены запроса
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error('Failed to save collections:', error);
    }
  };

  // Загрузка collections и API key при загрузке страницы
  useEffect(() => {
    const loadData = async () => {
      if (!isAuthenticated) {
        hasLoadedRef.current = true;
        isInitialLoadRef.current = false;
        return;
      }
      
      const token = localStorage.getItem('access_token');
      if (!token) {
        hasLoadedRef.current = true;
        isInitialLoadRef.current = false;
        return;
      }

      // Устанавливаем флаг загрузки ПЕРЕД началом запросов, чтобы предотвратить повторные вызовы
      hasLoadedRef.current = true;
      isInitialLoadRef.current = true;
      setIsLoadingApiKey(true);

      try {
        // Загружаем collections и API key параллельно
        const [collectionsResponse, apiKeyResponse] = await Promise.all([
          fetch(`${API_URL}/saved-collections`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }),
          fetch(`${API_URL}/auth/api-key`, {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          })
        ]);
        
        // Обрабатываем collections
        if (collectionsResponse.ok) {
          const collectionsData = await collectionsResponse.json();
          if (collectionsData.collections && collectionsData.collections.length > 0) {
            setCollections(collectionsData.collections);
          }
        }

        // Обрабатываем API key
        if (apiKeyResponse.ok) {
          const apiKeyData = await apiKeyResponse.json();
          if (apiKeyData.apiKey) {
            setApiKey(apiKeyData.apiKey);
          }
        }
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setIsLoadingApiKey(false);
        // Используем setTimeout чтобы дать React время обработать setCollections
        setTimeout(() => {
          isInitialLoadRef.current = false;
        }, 200);
      }
    };

    if (!hasLoadedRef.current) {
      loadData();
    }
  }, [isAuthenticated]); // Убрали setApiKey из зависимостей, так как функция стабильна

  // Автоматическое сохранение при изменении collections
  useEffect(() => {
    // Пропускаем сохранение если:
    // 1. Еще не завершена загрузка
    // 2. Это первая загрузка (isInitialLoadRef еще true)
    // 3. Пользователь не аутентифицирован
    if (!hasLoadedRef.current || isInitialLoadRef.current || !isAuthenticated) {
      return;
    }

    // Проверяем, действительно ли collections изменились
    const currentCollectionsString = JSON.stringify(collections);
    if (currentCollectionsString === lastSavedCollectionsRef.current) {
      return; // Collections не изменились, не сохраняем
    }

    // Отменяем предыдущий запрос, если он еще выполняется
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Создаем новый AbortController для этого запроса
    abortControllerRef.current = new AbortController();

    // Очищаем предыдущий таймер
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    // Сохраняем через 500ms после последнего изменения (debounce)
    saveTimeoutRef.current = setTimeout(() => {
      // Дополнительная проверка перед сохранением
      if (hasLoadedRef.current && !isInitialLoadRef.current && isAuthenticated) {
        const collectionsString = JSON.stringify(collections);
        // Проверяем еще раз, что collections не изменились за время ожидания
        if (collectionsString === currentCollectionsString) {
          saveCollections(collections, abortControllerRef.current?.signal).then(() => {
            // Обновляем последнее сохраненное значение только после успешного сохранения
            lastSavedCollectionsRef.current = collectionsString;
          });
        }
      }
    }, 500);

    // Очистка таймера и отмена запроса при размонтировании
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [collections, isAuthenticated]);

  // Обертка для setCollections с автоматическим сохранением
  const setCollectionsWithSave = (newCollections: Collection[] | ((prev: Collection[]) => Collection[])) => {
    if (typeof newCollections === 'function') {
      setCollections((prev) => {
        const updated = newCollections(prev);
        return updated;
      });
    } else {
      setCollections(newCollections);
    }
  };

  // Показываем загрузку пока проверяется аутентификация
  if (isAuthLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-100">
      {/* Header with buttons */}
      <div className="absolute top-4 right-4 z-10 flex space-x-3">
        {!isAuthenticated && (<></>)}
      </div>

      {/* Settings Menu (Bottom Left) */}
      {isAuthenticated && (
        <div className="fixed bottom-4 left-4 z-10" ref={settingsMenuRef}>
          <button
            onClick={() => setShowSettingsMenu(!showSettingsMenu)}
            className="w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-lg hover:bg-gray-50 transition-colors cursor-pointer"
            style={{
              border: '1px solid #e5e7eb'
            }}
          >
            <svg
              className="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
          </button>

          {/* Dropdown Menu */}
          {showSettingsMenu && (
            <div
              className="absolute bottom-14 left-0 bg-white rounded-lg shadow-xl border border-gray-200 min-w-[200px] overflow-hidden"
              style={{
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
              }}
            >
              <div className="py-1">
                <button
                  onClick={() => {
                    handleLogout();
                    setShowSettingsMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-3 transition-colors cursor-pointer"
                >
                  <svg
                    className="w-4 h-4 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  <span>Sign out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Demo overlay - серый фон поверх контента но под выделенными элементами */}
      {showDemo && (
        <div 
          className="fixed inset-0 pointer-events-none"
          style={{ 
            backgroundColor: 'rgba(0, 0, 0, 0.15)',
            zIndex: 40,
            transition: 'opacity 0.3s ease'
          }}
        />
      )}

      {/* Sidebar */}
      <div
        style={{ 
          width: 280,
          overflow: 'hidden',
          flexShrink: 0
        }}
        className="h-full"
      >
        <Sidebar
          collections={collections}
          setCollections={setCollectionsWithSave}
          newCollectionCount={newCollectionCount}
          setNewCollectionCount={setNewCollectionCount}
          openTabs={openTabs}
          setOpenTabs={setOpenTabs}
          activeTabId={activeTabId}
          setActiveTabId={setActiveTabId}
          showDemo={showDemo}
          demoStep={currentDemoStep}
        />
      </div>
      
      {/* Request Panel or Overview */}
      <div 
        style={{ 
          width: 'calc(-280px + 100vw)',
          minWidth: 0,
          flexShrink: 1
        }}
      >
        {/* Показываем Overview только если нет коллекций И (не в демо ИЛИ не аутентифицирован без демо) */}
        {collections.length === 0 && !showDemo ? (
          <Overview onStartDemo={handleDemoStart} />
        ) : (
          <RequestPanel
            collections={collections}
            setCollections={setCollectionsWithSave}
            openTabs={openTabs}
            setOpenTabs={setOpenTabs}
            activeTabId={activeTabId}
            setActiveTabId={setActiveTabId}
            showDemo={showDemo}
            isAuthenticated={isAuthenticated}
          />
        )}
      </div>

      {/* Element Highlighter Demo */}
      <ElementHighlighter
        isVisible={showDemo}
        onComplete={handleDemoComplete}
        onStepChange={setCurrentDemoStep}
      />
    </div>
  );
} 